import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useNavigate, useParams } from "react-router-dom";
import { FaArrowLeft, FaSpinner } from "react-icons/fa";
import { FiSend } from "react-icons/fi";
import useApp from "../../hooks/useAppContext";
import useAuth from "../../hooks/useAuthContext";
import { formatarHora } from "../../services/conversaoTimer";
import "../../styles/bg-responsive.css";
import "./chat.css";

const Conversa = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const {
    getMensagensChat,
    enviaMenagem,
    visualizarMenagem,
    handleImagemParticipante,
  } = useApp();
  const { usuario } = useAuth();

  const [mensagens, setMensagens] = useState([]);
  const [texto, setTexto] = useState("");
  const [usuarioConversa, setUsuarioConversa] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [enviando, setEnviando] = useState(false);

  const pollRef = useRef(null);
  const marcadasRef = useRef(new Set());
  const listaRef = useRef(null);
  const inputRef = useRef(null);
  const idDestino = parseInt(id, 10);

  const mensagensOrdenadas = useMemo(() => {
    return [...(mensagens || [])].sort((a, b) => {
      const dataA = new Date(a?.created_at || 0).getTime();
      const dataB = new Date(b?.created_at || 0).getTime();
      if (dataA !== dataB) return dataA - dataB;
      return Number(a?.id || 0) - Number(b?.id || 0);
    });
  }, [mensagens]);

  const scrollParaFim = useCallback((suave = false) => {
    const el = listaRef.current;
    if (!el) return;
    el.scrollTo({
      top: el.scrollHeight,
      behavior: suave ? "smooth" : "auto",
    });
  }, []);

  const carregarMensagens = useCallback(
    async ({ silent = false } = {}) => {
      if (!idDestino || Number.isNaN(idDestino)) return;

      try {
        if (!silent) setIsLoading(true);
        const resp = await getMensagensChat(idDestino);
        const lista = resp?.dados?.[0]?.chatMensagens || [];
        setMensagens(lista);
        if (resp?.usuario) setUsuarioConversa(resp.usuario);
      } catch (err) {
        console.error("Erro ao carregar conversa:", err);
      } finally {
        if (!silent) setIsLoading(false);
      }
    },
    [getMensagensChat, idDestino]
  );

  useEffect(() => {
    carregarMensagens();
    pollRef.current = setInterval(() => {
      carregarMensagens({ silent: true });
    }, 10000);

    return () => {
      if (pollRef.current) clearInterval(pollRef.current);
    };
  }, [carregarMensagens]);

  useEffect(() => {
    const pendentes = mensagensOrdenadas.filter(
      (m) =>
        m.visualizada_em == null &&
        Number(m.id_usuario) === idDestino &&
        !marcadasRef.current.has(m.id)
    );
    if (!pendentes.length) return;

    pendentes.forEach((m) => {
      marcadasRef.current.add(m.id);
      visualizarMenagem(m.id);
    });
  }, [mensagensOrdenadas, idDestino, visualizarMenagem]);

  useEffect(() => {
    if (!isLoading) scrollParaFim();
  }, [mensagensOrdenadas.length, isLoading, scrollParaFim]);

  const voltar = () => {
    if (pollRef.current) clearInterval(pollRef.current);
    setTexto("");
    navigate(-1);
  };

  const handleEnviaMensagem = async () => {
    const mensagem = texto.trim();
    if (!mensagem || enviando) return;

    try {
      setEnviando(true);
      await enviaMenagem(idDestino, mensagem);
      setTexto("");
      await carregarMensagens({ silent: true });
      scrollParaFim(true);
      inputRef.current?.focus();
    } finally {
      setEnviando(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleEnviaMensagem();
    }
  };

  return (
    <div className="main chat-conversa-page">
      <div className="chat-conversa chat-conversa-bg">
        <div className="chat-conversa-overlay" aria-hidden />

        <header className="chat-conversa-header">
          <button
            type="button"
            className="chat-conversa-back"
            onClick={voltar}
            title="Voltar"
            aria-label="Voltar"
          >
            <FaArrowLeft size={16} aria-hidden />
          </button>
          <img
            className="chat-conversa-avatar"
            src={handleImagemParticipante(usuarioConversa)}
            alt=""
          />
          <div className="chat-conversa-user">
            <strong>{usuarioConversa?.nome || "Conversa"}</strong>
            <span>Chat privado</span>
          </div>
        </header>

        <div className="chat-conversa-body" ref={listaRef}>
          {isLoading ? (
            <div className="chat-conversa-loading">
              <FaSpinner className="fa-spin" size={24} aria-hidden />
              <span>Carregando mensagens…</span>
            </div>
          ) : mensagensOrdenadas.length === 0 ? (
            <div className="chat-conversa-empty">
              <p>Nenhuma mensagem ainda</p>
              <span>Envie a primeira mensagem para iniciar a conversa.</span>
            </div>
          ) : (
            <ul className="chat-conversa-lista" aria-label="Mensagens">
              {mensagensOrdenadas.map((m) => {
                const minha = Number(m.id_usuario) === Number(usuario?.id);
                const hora = m.created_at
                  ? formatarHora(String(m.created_at))?.substring(0, 5)
                  : "";

                return (
                  <li
                    key={m.id}
                    className={`chat-bolha-row${minha ? " is-mine" : ""}`}
                  >
                    <div
                      className={`chat-bolha${minha ? " chat-bolha--mine" : " chat-bolha--other"}`}
                    >
                      <p className="chat-bolha-texto">{m.mensagem}</p>
                      {hora ? (
                        <time className="chat-bolha-hora" dateTime={m.created_at}>
                          {hora}
                        </time>
                      ) : null}
                    </div>
                  </li>
                );
              })}
            </ul>
          )}
        </div>

        <footer className="chat-conversa-footer">
          <div className="chat-conversa-composer">
            <input
              ref={inputRef}
              type="text"
              className="chat-conversa-input"
              value={texto}
              onChange={(e) => setTexto(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder="Digite uma mensagem"
              aria-label="Digite uma mensagem"
              disabled={enviando}
              autoComplete="off"
            />
            <button
              type="button"
              className="chat-conversa-send"
              onClick={handleEnviaMensagem}
              disabled={enviando || !texto.trim()}
              title="Enviar"
              aria-label="Enviar mensagem"
            >
              {enviando ? (
                <FaSpinner className="fa-spin" size={16} aria-hidden />
              ) : (
                <FiSend size={18} aria-hidden />
              )}
            </button>
          </div>
        </footer>
      </div>
    </div>
  );
};

export default Conversa;
